import { useTranslation } from 'react-i18next';

import { Avatar } from '@/shared/components/avatar';
import { cn } from '@/shared/lib/cn';

import { CHANNEL_ICON } from '../lib/channelIcons';
import { CHANNEL_TONE, channelLabel, customerName } from '../lib/format';
import type { ChannelType, CustomerBrief } from '../types';

/** The channel of a dialog as a small tinted tile (with its name as tooltip text). */
export function ChannelIcon({
  channel,
  size = 'sm',
  className,
}: {
  channel: ChannelType;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
}) {
  const { t } = useTranslation();
  const Icon = CHANNEL_ICON[channel];
  return (
    <span
      title={channelLabel(channel, t)}
      data-channel={channel}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-md',
        CHANNEL_TONE[channel],
        size === 'xs'
          ? 'size-4 [&_svg]:size-2.5'
          : size === 'sm'
            ? 'size-5 [&_svg]:size-3'
            : 'size-7 [&_svg]:size-4',
        className,
      )}
    >
      <Icon aria-hidden />
    </span>
  );
}

/** A client's avatar with the channel badge in the corner. */
export function CustomerAvatar({
  customer,
  size = 'lg',
  className,
}: {
  customer: Pick<CustomerBrief, 'id' | 'name' | 'username' | 'avatarUrl' | 'channel'>;
  size?: 'md' | 'lg' | 'xl';
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <span className={cn('relative inline-flex shrink-0', className)}>
      <Avatar name={customerName(customer, t)} src={customer.avatarUrl} seed={customer.id} size={size} />
      <ChannelIcon
        channel={customer.channel}
        size="xs"
        className="absolute -right-1 -bottom-1 ring-2 ring-background"
      />
    </span>
  );
}
